import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

import { AiAssistantService } from './ai-assistant.service';

export interface PromptCardPollingUpdate {
  promptId: number;
  data: any;
}

interface PromptPollingState {
  attempts: number;
  executionId?: number;
}

@Injectable({
  providedIn: 'root'
})
export class PromptCardPollingService {
  private static readonly MAX_ATTEMPTS = 30;
  private static readonly INTERVAL_MS = 10000;

  private readonly updatesSubject = new Subject<PromptCardPollingUpdate>();
  readonly updates$ = this.updatesSubject.asObservable();

  private readonly active = new Map<number, PromptPollingState>();
  private readonly latestByPromptId = new Map<number, any>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private inFlight = false;

  constructor(private aiService: AiAssistantService) {}

  trackPrompt(promptId: number, executionId?: number | null): void {
    const existing = this.active.get(promptId);
    if (existing) {
      if (executionId && executionId !== existing.executionId) {
        existing.executionId = executionId;
        existing.attempts = 0;
      }
    } else {
      this.active.set(promptId, {
        attempts: 0,
        executionId: executionId || undefined
      });
    }

    this.ensureTimer();
    this.pollOnce();
  }

  getLatest(promptId: number): any | undefined {
    return this.latestByPromptId.get(promptId);
  }

  private normalizeExecutionStatus(status?: string): string | undefined {
    if (!status) return undefined;
    const normalized = status.toLowerCase().replace(/[\s_-]+/g, '');
    if (normalized === 'pending' || normalized === 'queued') return 'Pending';
    if (normalized === 'running' || normalized === 'alreadyrunning') {
      return 'Running';
    }
    if (normalized === 'completed' || normalized === 'alreadycompleted') {
      return 'Completed';
    }
    if (normalized === 'failed' || normalized === 'error') return 'Failed';
    return status;
  }

  private ensureTimer(): void {
    if (this.timer) return;
    this.timer = setInterval(() => this.pollOnce(), PromptCardPollingService.INTERVAL_MS);
  }

  private clearTimer(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async pollOnce(): Promise<void> {
    if (this.inFlight) return;
    if (this.active.size === 0) {
      this.clearTimer();
      return;
    }

    this.inFlight = true;
    try {
      const entries = Array.from(this.active.entries());
      for (const [promptId, state] of entries) {
        if (state.attempts >= PromptCardPollingService.MAX_ATTEMPTS) {
          await this.handleTimeout(promptId, state);
          continue;
        }

        state.attempts += 1;
        try {
          const response = await this.aiService.getPromptCardPreviewByPromptCardId(promptId);
          const data = (response as any)?.data || response;
          const status = this.normalizeExecutionStatus(data?.status);
          const normalizedData = status ? { ...data, status } : data;

          if (normalizedData?.executionId && !state.executionId) {
            state.executionId = normalizedData.executionId;
          }

          this.latestByPromptId.set(promptId, normalizedData);
          this.updatesSubject.next({ promptId, data: normalizedData });

          const isDone =
            status === 'Completed' ||
            status === 'Failed' ||
            Boolean(normalizedData?.completedAt) ||
            Boolean(normalizedData?.answer);

          if (isDone) {
            this.active.delete(promptId);
          }
        } catch {
          // Ignore polling errors for this prompt
        }
      }
    } finally {
      this.inFlight = false;
      if (this.active.size === 0) {
        this.clearTimer();
      }
    }
  }

  private async handleTimeout(promptId: number, state: PromptPollingState): Promise<void> {
    this.active.delete(promptId);

    if (state.executionId) {
      try {
        await this.aiService.failPromptCardExecution(
          state.executionId,
          'Prompt execution timed out. Please regenerate.'
        );
      } catch {
        // Ignore backend timeout update failures
      }
    }

    this.updatesSubject.next({
      promptId,
      data: {
        status: 'Failed',
        errorMessage: 'Prompt execution timed out. Please regenerate.'
      }
    });
    this.latestByPromptId.set(promptId, {
      status: 'Failed',
      errorMessage: 'Prompt execution timed out. Please regenerate.'
    });
  }
}
